<template>
    <icon-button
        v-if="!textBtn"
        :disabled="isDisabled"
        :btn-text="btnText"
        icon="get_app"
        @click="downloadResource"
    />
    <v-btn
        v-else-if="isSecondaryBtn"
        :disabled="isDisabled"
        secondary
        variant="outlined"
        class="secondary-btn"
        @click="downloadResource"
    >
        {{ toSentenceCase(btnText) }}
        <icon icon-name="custom:download" right small />
    </v-btn>
    <v-btn v-else primary variant="outlined" class="primary-btn" @click="downloadResource">
        {{ toSentenceCase(btnText) }}
        <icon icon-name="custom:download" right small />
    </v-btn>
</template>

<script setup>
import { ref, computed } from 'vue';
import { camelCase, get } from 'lodash';
import { useStore } from 'vuex';
// $t is only auto-available in templates; script code needs the i18n instance directly
import i18n from '@/js/vue-i18n';

const props = defineProps({
    resource: String,
    where: {
        type: Object,
        default: () => {},
    },
    format: String,
    label: {
        type: String,
        default: null,
    },
    extraParams: Object,
    textBtn: {
        type: Boolean,
        default: false,
    },
    downloadAction: {
        type: String,
        default: 'download',
    },
    exportSchema: String,
    isSecondaryBtn: {
        type: Boolean,
        default: false,
    },
});

const store = useStore();

const isDisabled = ref(false);

const computedAction = computed(() => `${camelCase(props.resource)}/${props.downloadAction}`);

const btnText = computed(() => {
    const format = i18n.global.t(`general.exportFormats.${props.format}`);
    if (props.label) {
        return `${props.label}`;
    }
    return `${i18n.global.t('general.labels.download')} ${props.resource} ${format}`;
});

// validated once at creation, matches the original created() guard
const isValidDownloadAction = !!get(store, `_actions.${computedAction.value}`);
if (!isValidDownloadAction) {
    throw new Error(
        i18n.global.t('validation.failureMessages.downloadResourceAction', {
            downloadResourceAction: computedAction.value,
        })
    );
}

const downloadResource = async () => {
    isDisabled.value = true;
    await store.dispatch(computedAction.value, {
        params: {
            resource: props.resource,
            export: true,
            where: props.where,
            exportFormat: props.format,
            exportSchema: props.exportSchema,
            ...props.extraParams,
        },
    });
    isDisabled.value = false;
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.primary-btn {
    :deep(#download-icon) {
        fill: $promo-white;
    }
}

.secondary-btn {
    :deep(#download-icon) {
        fill: $promo-primary-colour;
    }
}
</style>
